/**
 * Latest Casino Wins – Snapshot Renderer
 *
 * Listens for periodic snapshot updates from the backend (via Echo),
 * replaces the entire visible list, and applies a visual "flash" animation
 * only to entries that are new or whose values have changed compared
 * to the previous snapshot.
 */

type LatestWin = {
    key: string;
    slug: string;
    game_name: string;
    image_url: string;
    display_email: string;
    email: string;
    name: string;
    formatted_amount: string;
};

type LatestWinsSnapshotPayload = {
    mode?: 'snapshot';
    items?: LatestWin[];
};

type LatestWinsEchoEvent = {
    wins?: LatestWinsSnapshotPayload;
};
let hasRenderedOnce = false;
const MAX_VISIBLE_WINS = 10;
const FLASH_CLASS = 'latest-win-flash';

const previousByKey = new Map<string, string>();

document.addEventListener('DOMContentLoaded', initLatestWins);

function initLatestWins(): void {
    const container = resolveContainer();
    const template = document.getElementById('latest-win-template') as HTMLTemplateElement | null;
    if (!container || !template) return;

    connectToEcho(container, template);
}

function resolveContainer(): HTMLElement | null {
    const byId = document.getElementById('latest-wins-container');
    if (byId?.classList.contains('scroll-wrapper')) return byId;

    return document.querySelector('.leaderboard-container .scroll-wrapper');
}

function connectToEcho(container: HTMLElement, template: HTMLTemplateElement): void {
    let attempts = 0;
    const maxAttempts = 50;
    const retryDelay = 200;

    const interval = setInterval(() => {
        if (window.Echo) {
            clearInterval(interval);

            window.Echo.channel('casino.latest-wins').listen(
                '.latest-wins-snapshot-updated',
                (event: LatestWinsEchoEvent) => {
                    const payload = event?.wins;
                    if (!payload) return;

                    const items = Array.isArray(payload.items) ? payload.items : [];
                    renderSnapshotWithFlash(container, template, items);
                }
            );

            return;
        }

        if (++attempts >= maxAttempts) clearInterval(interval);
    }, retryDelay);
}

function renderSnapshotWithFlash(container: HTMLElement, template: HTMLTemplateElement, items: LatestWin[]): void {
    const visible = items.slice(0, MAX_VISIBLE_WINS);

    const shouldFlashKeys = new Set<string>();
    const nextByKey = new Map<string, string>();

    visible.forEach((win) => {
        const signature = buildSignature(win);
        nextByKey.set(win.key, signature);
        if (!hasRenderedOnce) return;

        const prevSignature = previousByKey.get(win.key);

        if (prevSignature === undefined) {
            shouldFlashKeys.add(win.key);
            return;
        }

        if (prevSignature !== signature) {
            shouldFlashKeys.add(win.key);
        }
    });

    container.innerHTML = '';

    visible.forEach((win) => {
        const link = createCardElement(template, win);
        if (!link) return;

        container.appendChild(link);

        if (shouldFlashKeys.has(win.key)) {
            const card = link.querySelector('.player-card') as HTMLElement | null;
            if (card) animateFlash(card);
        }
    });

    previousByKey.clear();
    nextByKey.forEach((v, k) => previousByKey.set(k, v));
    hasRenderedOnce = true;
}

function buildSignature(win: LatestWin): string {
    return `${win.formatted_amount}`;
}

function createCardElement(template: HTMLTemplateElement, win: LatestWin): HTMLAnchorElement | null {
    const clone = template.content.cloneNode(true) as DocumentFragment;
    const link = clone.querySelector('a') as HTMLAnchorElement | null;
    const card = clone.querySelector('.player-card') as HTMLElement | null;

    if (!link || !card) return null;

    if (win.slug) {
        link.href = link.href.replace('__slug__', win.slug);
    }

    updateCardContent(card, win);
    return link;
}

function updateCardContent(card: HTMLElement, win: LatestWin): void {
    card.setAttribute('data-win-key', win.key);

    const nameEl = card.querySelector('.player-name');
    if (nameEl) nameEl.textContent = win.display_email || 'Anonymous';

    const gameNameEl = card.querySelector('.player-game-name');
    if (gameNameEl) gameNameEl.textContent = win.game_name || 'Casino game';

    const amountEl = card.querySelector('.player-amount');
    if (amountEl) amountEl.textContent = `+${win.formatted_amount}`;

    const image = card.querySelector('.player-image') as HTMLElement | null;
    if (image) {
        image.style.backgroundImage = win.image_url ? `url('${win.image_url}')` : '';
        image.style.backgroundSize = win.image_url ? 'cover' : '';
    }
}

function animateFlash(element: HTMLElement): void {
    element.classList.remove(FLASH_CLASS);
    requestAnimationFrame(() => requestAnimationFrame(() => element.classList.add(FLASH_CLASS)));
}
